import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "../../../../utils/styles";
import { AppleLogo, BoletoIcon, CardBrandMark, CardIcon, GoogleLogo, PixIcon } from "./icons";
import { CardErrors, CardState, StripeCardFields } from "./StripeCard";
import { MethodList } from "./styles";

export type Plan = "MONTHLY" | "ANNUALLY";
export type PaymentMethod = "card" | "applePay" | "googlePay" | "pix" | "boleto";

type Props = {
  /** Leave out for a one-time purchase (a domain): every method, no plan rule. */
  plan?: Plan;
  /** Pix and boleto are Brazilian: only offered to buyers from Brazil. */
  isBrazil: boolean;
  /** The wallets this device has (Stripe tells): the others aren't offered. Leave out to offer none. */
  availableWallets?: { applePay: boolean; googlePay: boolean };
  /** Let a boleto expire unpaid: boleto isn't offered until one of theirs clears. */
  isBoletoBlocked?: boolean;
  /** Nothing picked yet: every method closed. */
  method?: PaymentMethod;
  onMethodChange: (method: PaymentMethod) => void;
  card: CardState;
  onCardChange: (card: CardState) => void;
  cardErrors: CardErrors;
  /** While paying: the card's fields (Stripe's, which a fieldset doesn't reach) don't take typing. */
  disabled?: boolean;
  onSwitchToAnnual?: () => void;
  /** Inside Apple Pay or Google Pay, once picked: that wallet's own button. */
  walletFields?: (wallet: "applePay" | "googlePay") => ReactNode;
  /** Under Pix and boleto, once picked: the details each needs (CPF/CNPJ; plus the address for boleto). */
  pixFields?: ReactNode;
  boletoFields?: ReactNode;
  /** Pix and boleto only for the renewals (managing a subscription): no "pay now" wording. */
  isForRenewal?: boolean;
  /** What happens with each method, when what's bought isn't the PLUS (a domain): replaces the PLUS wording. */
  info?: Partial<Record<PaymentMethod, string>>;
  /** The line under each method's name, likewise (e.g. a one-time purchase doesn't renew by itself). */
  captions?: Partial<Record<PaymentMethod, string>>;
};

export const PaymentMethods = ({
  availableWallets,
  isBoletoBlocked,
  plan,
  isBrazil,
  method,
  onMethodChange,
  card,
  onCardChange,
  cardErrors,
  disabled,
  onSwitchToAnnual,
  walletFields,
  pixFields,
  boletoFields,
  isForRenewal,
  info,
  captions,
}: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.subscription.checkout";
  const methods: { key: PaymentMethod; icon: JSX.Element }[] = [
    { key: "card", icon: <CardIcon /> },
    // Each wallet only where the device has it (Apple Pay: Safari on Apple devices; Google Pay: Chrome/Android
    // with a card saved to Google)
    ...(availableWallets?.applePay ? [{ key: "applePay" as const, icon: <AppleLogo width={18} height={18} /> }] : []),
    ...(availableWallets?.googlePay
      ? [{ key: "googlePay" as const, icon: <GoogleLogo width={18} height={18} /> }]
      : []),
    ...(isBrazil
      ? [
          { key: "pix" as const, icon: <PixIcon /> },
          ...(isBoletoBlocked ? [] : [{ key: "boleto" as const, icon: <BoletoIcon /> }]),
        ]
      : []),
  ];

  return (
    <MethodList role="radiogroup" aria-label={t(`${prefix}.methods.label`)}>
      {methods.map(({ key, icon }) => {
        // Pix and boleto pay a whole year at once: the monthly plan is card-only
        const isLocked = (key === "pix" || key === "boleto") && plan === "MONTHLY";
        const isSelected = method === key;
        return (
          <li key={key} className={classNames({ selected: isSelected, locked: isLocked })}>
            <button
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-disabled={isLocked}
              className="method"
              onClick={() => !isLocked && onMethodChange(key)}>
              <span className="radio" aria-hidden="true" />
              <span className="icon">{icon}</span>
              <span className="text">
                <b>{t(`${prefix}.methods.${key}.title`)}</b>
                <span>
                  {isLocked
                    ? t(`${prefix}.methods.annualOnly`)
                    : (captions?.[key] ?? t(`${prefix}.methods.${key}.caption`))}
                </span>
              </span>
              {key === "card" && (
                <span className="brands">
                  <CardBrandMark brand="visa" />
                  <CardBrandMark brand="mastercard" />
                  <CardBrandMark brand="elo" />
                  <CardBrandMark brand="amex" />
                </span>
              )}
              {key === "pix" && !isLocked && <span className="tag">{t(`${prefix}.methods.pix.tag`)}</span>}
            </button>
            {/* Right in the locked box: the way to turn it on */}
            {isLocked && onSwitchToAnnual && (
              <button type="button" className="switch" onClick={onSwitchToAnnual}>
                {t(`${prefix}.methods.switchToAnnual`)}
              </button>
            )}

            {isSelected && key === "card" && (
              <StripeCardFields card={card} onChange={onCardChange} errors={cardErrors} disabled={disabled} />
            )}

            {isSelected && key !== "card" && (
              <p className="info">
                {info?.[key] ?? t(`${prefix}.methods.${key}.${isForRenewal ? "renewalInfo" : "info"}`)}
              </p>
            )}
            {isSelected && (key === "applePay" || key === "googlePay") && walletFields && (
              <div className="extra">{walletFields(key)}</div>
            )}
            {isSelected && key === "pix" && pixFields && <div className="extra">{pixFields}</div>}
            {isSelected && key === "boleto" && boletoFields && <div className="extra">{boletoFields}</div>}
          </li>
        );
      })}
    </MethodList>
  );
};
